
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import StationService from "../../services/station.service";

// Common error handler
const getErrorMessage = (error) => {
  return (
    error.response?.data?.message ||
    error.message ||
    "Something went wrong"
  );
};

// GET ALL STATIONS
export const getStations = createAsyncThunk(
  "station/getStations",
  async (params = {}, thunkAPI) => {
    try {
      const response = await StationService.getStations(params);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch stations"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// GET STATION BY ID
export const getStationById = createAsyncThunk(
  "station/getStationById",
  async (id, thunkAPI) => {
    try {
      const response = await StationService.getStationById(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch station details"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// CREATE STATION
export const createStation = createAsyncThunk(
  "station/createStation",
  async (payload, thunkAPI) => {
    try {
      const response = await StationService.createStation(payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to create station"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// UPDATE STATION
export const updateStation = createAsyncThunk(
  "station/updateStation",
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await StationService.updateStation(id, payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to update station"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// DELETE STATION
export const deleteStation = createAsyncThunk(
  "station/deleteStation",
  async (id, thunkAPI) => {
    try {
      const response = await StationService.deleteStation(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to delete station"
        );
      }

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  stations: [],
  selectedStation: null,

  loading: false,
  detailsLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  error: null,
  detailsError: null,
  successMessage: null,

  pagination: {
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  },
};

const stationSlice = createSlice({
  name: "station",
  initialState,

  reducers: {
    clearStationError: (state) => {
      state.error = null;
      state.detailsError = null;
    },

    clearStationMessage: (state) => {
      state.successMessage = null;
    },

    clearSelectedStation: (state) => {
      state.selectedStation = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // GET ALL STATIONS
      .addCase(getStations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getStations.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        // API response: data.stations
        state.stations = data.stations || [];

        state.pagination = {
          total: data.total ?? 0,
          currentPage: data.currentPage ?? 1,
          totalPages: data.totalPages ?? 0,
          limit: data.limit ?? 10,
        };
      })

      .addCase(getStations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // GET STATION BY ID
      .addCase(getStationById.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })

      .addCase(getStationById.fulfilled, (state, action) => {
        state.detailsLoading = false;

        const data = action.payload || {};
        state.selectedStation = data.station || data;
      })

      .addCase(getStationById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload || action.error.message;
      })

      // CREATE STATION
      .addCase(createStation.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(createStation.fulfilled, (state, action) => {
        state.createLoading = false;

        state.successMessage =
          action.payload?.message || "Station created successfully";

        const data = action.payload?.data;
        const station = data?.station || data;

        if (station?._id) {
          state.stations.unshift(station);
          state.pagination.total += 1;
        }
      })

      .addCase(createStation.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || action.error.message;
      })

      // UPDATE STATION
      .addCase(updateStation.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(updateStation.fulfilled, (state, action) => {
        state.updateLoading = false;

        state.successMessage =
          action.payload?.message || "Station updated successfully";

        const data = action.payload?.data;
        const updatedStation = data?.station || data;

        if (updatedStation?._id) {
          const index = state.stations.findIndex(
            (station) => station._id === updatedStation._id
          );

          if (index !== -1) {
            state.stations[index] = updatedStation;
          }

          if (state.selectedStation?._id === updatedStation._id) {
            state.selectedStation = updatedStation;
          }
        }
      })

      .addCase(updateStation.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // DELETE STATION
      .addCase(deleteStation.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(deleteStation.fulfilled, (state, action) => {
        state.deleteLoading = false;

        state.stations = state.stations.filter(
          (station) => station._id !== action.payload
        );

        if (state.selectedStation?._id === action.payload) {
          state.selectedStation = null;
        }

        state.pagination.total = Math.max(
          0,
          state.pagination.total - 1
        );

        state.successMessage = "Station deleted successfully";
      })

      .addCase(deleteStation.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  clearStationError,
  clearStationMessage,
  clearSelectedStation,
} = stationSlice.actions;

export default stationSlice.reducer;
